import { useCallback, useEffect, useState } from 'react';
import type { Item, ItemKind } from '../types';
import { api } from '../api';
import { useI18n } from '../i18n';
import { QuickCapture } from './QuickCapture';
import { ItemList } from './ItemList';

const FILTERS: { id: ItemKind | 'all'; labelKey: string }[] = [
  { id: 'all', labelKey: 'inbox.filter.all' },
  { id: 'task', labelKey: 'inbox.filter.task' },
  { id: 'note', labelKey: 'inbox.filter.note' },
  { id: 'idea', labelKey: 'inbox.filter.idea' },
];

export function Inbox() {
  const { t } = useI18n();
  const [items, setItems] = useState<Item[]>([]);
  const [filter, setFilter] = useState<ItemKind | 'all'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items } = await api.items(filter === 'all' ? {} : { kind: filter });
      setItems(items);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('inbox.title')}</h1>
          <div className="page-sub">{t('inbox.subtitle')}</div>
        </div>
      </header>

      <section className="card">
        <h2 className="card-title">{t('inbox.section.capture')}</h2>
        <QuickCapture onCaptured={load} />
      </section>

      <section className="card">
        <div className="row-between" style={{ marginBottom: 14 }}>
          <h2 className="card-title" style={{ margin: 0 }}>
            {t('inbox.section.memory')} ({items.length})
          </h2>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {FILTERS.map((f) => (
              <button
                key={f.id}
                className={`btn ${filter === f.id ? 'btn-primary' : ''}`}
                onClick={() => setFilter(f.id)}
                style={{ padding: '6px 14px', fontSize: 13 }}
              >
                {t(f.labelKey)}
              </button>
            ))}
          </div>
        </div>
        {error && <div className="error-banner">{error}</div>}
        {loading ? (
          <div className="empty">{t('inbox.loading')}</div>
        ) : (
          <ItemList items={items} emptyHint={t('inbox.empty')} />
        )}
      </section>
    </>
  );
}
